import React, { useState, useEffect } from "react";
import { Box, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logoImg from "../../../../design-system/assets/brand/logo.png";
import { LanguageSwitcherCompact } from "../../../i18n/LanguageSwitcher.jsx";

// NAV_LINKS built inside component so labels use t()

const HEADER_H = 72;

export default function Header() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("home");
  const NAV_LINKS = [
    { labelKey: "headerNavProduct",   hasArrow: true,  anchor: "#features" },
    { labelKey: "headerNavSolutions", hasArrow: true,  anchor: "#features" },
    { labelKey: "headerNavResources", hasArrow: true,  anchor: "#features" },
    { labelKey: "navPricing",         hasArrow: false, route:  "/offers"   },
    { labelKey: "headerNavCompany",   hasArrow: true,  anchor: "#contact"  },
  ];
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (item) => {
    if (item.route) {
      navigate(item.route);
    } else if (item.anchor) {
      const el = document.querySelector(item.anchor);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Box
      component="header"
      sx={{
        position: "fixed",
        insetInline: 0,
        top: 0,
        zIndex: 60,
        height: HEADER_H,
        display: "flex",
        alignItems: "center",
        transition: "background 0.28s ease, box-shadow 0.28s ease",
        background: scrolled ? "rgba(255,255,255,0.94)" : "transparent",
        backdropFilter: scrolled ? "blur(18px) saturate(1.6)" : "none",
        boxShadow: scrolled ? "0 1px 0 #E7E9F4, 0 4px 20px rgba(46,55,120,.06)" : "none",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1320,
          mx: "auto",
          px: { xs: 3, md: "30px" },
          display: "flex",
          alignItems: "center",
          gap: "18px",
        }}
      >
        {/* Logo */}
        <Box
          component="img"
          src={logoImg}
          alt="Teleredo"
          sx={{ height: 34, width: "auto", flexShrink: 0, cursor: "pointer" }}
          onClick={() => navigate("/")}
        />

        {/* Nav links */}
        <Box
          component="nav"
          sx={{
            display: { xs: "none", md: "flex" },
            gap: "16px",
            ml: "6px",
            whiteSpace: "nowrap",
          }}
        >
          {NAV_LINKS.map((item) => (
            <Box
              key={item.labelKey}
              component="span"
              onClick={() => handleNav(item)}
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "var(--ink-500)",
                cursor: "pointer",
                fontFamily: "var(--font-ui)",
                transition: "color 0.15s",
                "&:hover": { color: "var(--brand-violet-ink)" },
              }}
            >
              {t(item.labelKey)}{item.hasArrow ? " ▾" : ""}
            </Box>
          ))}
        </Box>

        {/* Right CTA */}
        <Box sx={{ ml: "auto", display: "flex", gap: "10px", alignItems: "center", flexShrink: 0 }}>
          <LanguageSwitcherCompact variant="light" />
          <Button
            onClick={() => navigate("/signin")}
            sx={{
              height: 40,
              px: "18px",
              borderRadius: "20px",
              border: "1px solid rgba(180,185,210,.5)",
              background: "rgba(255,255,255,.6)",
              fontFamily: "var(--font-ui)",
              fontWeight: 600,
              fontSize: "14px",
              color: "var(--ink-700)",
              textTransform: "none",
              backdropFilter: "blur(4px)",
              "&:hover": { background: "rgba(255,255,255,.9)", borderColor: "#C4C9E4" },
            }}
          >
            {t("headerLogin")}
          </Button>
          <Button
            onClick={() => navigate("/signup")}
            sx={{
              height: 40,
              px: "20px",
              borderRadius: "20px",
              border: "none",
              background: "linear-gradient(135deg,#1FC0DA,#4B78EC 50%,#7A3FE4)",
              fontFamily: "var(--font-ui)",
              fontWeight: 600,
              fontSize: "14px",
              color: "#fff",
              boxShadow: "var(--shadow-cta)",
              textTransform: "none",
              display: { xs: "none", sm: "flex" },
              "&:hover": {
                background: "linear-gradient(135deg,#18AABF,#3B68D0 50%,#6632C8)",
                boxShadow: "0 10px 24px rgba(90,70,220,.38)",
                transform: "translateY(-1px)",
              },
            }}
          >
            {t("heroStartFree")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
